import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TextReveal } from "../motion/TextReveal";
import { buildWhatsAppLink } from "../../lib/whatsapp";
import { trackWhatsAppClickConversion } from "../../lib/gtag";
import { resolveMediaUrl } from "../../lib/api";

const TAGLINE_INITIAL = { y: "100%", opacity: 0 };
const TAGLINE_ANIMATE = { y: "0%", opacity: 1 };
const TAGLINE_TRANSITION = { duration: 0.9, delay: 0.6, ease: [0.16, 1, 0.3, 1] };
const CTA_INITIAL = { opacity: 0, y: 20 };
const CTA_ANIMATE = { opacity: 1, y: 0 };
const CTA_TRANSITION = { duration: 0.8, delay: 0.9 };
const SCROLL_HINT_INITIAL = { opacity: 0 };
const SCROLL_HINT_ANIMATE = { opacity: 1 };
const SCROLL_HINT_TRANSITION = { delay: 1.4 };

export const Hero = ({ content, onQuoteClick }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  const heroImageDesktop = content?.hero_image_desktop;
  const heroImageMobile = content?.hero_image_mobile || heroImageDesktop;
  const number = content?.whatsapp_number;

  return (
    <section id="inicio" ref={ref} className="relative h-[100vh] w-full overflow-hidden bg-black">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {heroImageDesktop && (
          <img
            src={resolveMediaUrl(heroImageDesktop)}
            alt="The Wedding Photographie"
            className="hidden lg:block h-full w-full object-cover"
            data-testid="hero-background-image-desktop"
          />
        )}
        {heroImageMobile && (
          <img
            src={resolveMediaUrl(heroImageMobile)}
            alt="The Wedding Photographie"
            className="lg:hidden h-full w-full object-cover"
            data-testid="hero-background-image-mobile"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 flex h-full flex-col items-start justify-end px-6 md:px-16 pb-24 max-w-5xl">
        <span className="text-twp-gold text-xs md:text-sm uppercase tracking-[0.35em] mb-6 font-medium">
          Bodas · XV Años · Bautizos · Sesiones
        </span>

        <h1 className="font-serif-display text-5xl sm:text-6xl md:text-8xl leading-[0.95] text-white mb-6">
          <TextReveal text={content?.hero_headline || "The Wedding Photographie"} />
        </h1>

        <div className="max-w-xl mb-10 overflow-hidden">
          <motion.p
            initial={TAGLINE_INITIAL}
            animate={TAGLINE_ANIMATE}
            transition={TAGLINE_TRANSITION}
            className="text-white/80 text-base md:text-lg font-light"
            data-testid="hero-tagline"
          >
            {content?.hero_tagline}
          </motion.p>
        </div>

        <motion.div
          initial={CTA_INITIAL}
          animate={CTA_ANIMATE}
          transition={CTA_TRANSITION}
          className="flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-quote-button"
            onClick={onQuoteClick}
            className="px-8 py-4 bg-twp-gold text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-colors duration-300"
          >
            Cotizar mi evento
          </button>
          {number && (
            <a
              data-testid="hero-whatsapp-button"
              href={buildWhatsAppLink(number, "Hola, me interesa información sobre sus servicios de fotografía y video para bodas, XV años y eventos en Querétaro.")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackWhatsAppClickConversion}
              className="px-8 py-4 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-semibold hover:border-twp-gold hover:text-twp-gold transition-colors duration-300"
            >
              Enviar WhatsApp
            </a>
          )}
        </motion.div>
      </motion.div>

      <motion.div
        initial={SCROLL_HINT_INITIAL}
        animate={SCROLL_HINT_ANIMATE}
        transition={SCROLL_HINT_TRANSITION}
        className="absolute bottom-8 right-8 z-10 hidden md:flex flex-col items-center gap-3"
      >
        <span className="text-white/40 text-[10px] uppercase tracking-[0.3em] rotate-90 origin-center translate-y-6">Scroll</span>
        <div className="w-px h-16 bg-gradient-to-b from-twp-gold to-transparent" />
      </motion.div>
    </section>
  );
};
